import type { ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import styles from './ApplyCouponAsAdminDialog.module.css'
import { Button, RawButton } from '@/components/Button'
import { Icon } from '@/components/Icon'
import { SearchField } from '@/components/SearchField'
import { dateFormatters } from '@/utils/date-util'

export interface ApplyCouponAsAdminDialogProps {
  showing: boolean
  onClose: () => unknown
  onApply: () => unknown
  isApplyPending: boolean
  isApplyDisabled: boolean
  onSearch: (searchTerm: string) => unknown
  isEmpty: boolean
  loader?: ReactNode
  children?: ReactNode
}

export function ApplyCouponAsAdminDialog({
  showing,
  onClose,
  onApply,
  isApplyPending,
  isApplyDisabled,
  onSearch,
  isEmpty,
  loader,
  children,
}: ApplyCouponAsAdminDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isApplyPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />

      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentTitle>
            <div className={styles.header}>Add coupon</div>
          </ModalSheetContentTitle>

          <ModalSheetContentDescription>
            <div className={styles.header}>
              Search for coupon by either coupon code or name.
            </div>
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>

        <div className={styles.searchContainer}>
          <SearchField
            autoFocus
            stretch
            testId="job-coupon-apply-search"
            className={styles.couponSearch}
            placeholder="Type to search"
            onChange={onSearch}
          />
        </div>

        <div className={styles.couponContainer}>
          {children}

          {isEmpty && (
            <div className={styles.empty}>
              <p>We couldn't find anything matching your description.</p>
              <p>Try a different search.</p>
            </div>
          )}

          {loader}
        </div>
      </ModalSheetContent>

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isApplyPending}
            onPress={onClose}
          />
          <Button
            label="Apply"
            variant="primary"
            isPending={isApplyPending}
            testId="apply-coupon-as-admin-submit-button"
            onPress={onApply}
            isDisabled={isApplyDisabled}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}

export interface ApplyCouponAsAdminDialogCouponItemProps {
  code: string
  dateExpires: Date | null
  description: string
  discount: string
  maxSavings: string | null
  isSelected: boolean
  onSelect: () => unknown
}

export function ApplyCouponAsAdminDialogCouponItem({
  code,
  dateExpires,
  description,
  discount,
  maxSavings,
  isSelected,
  onSelect,
}: ApplyCouponAsAdminDialogCouponItemProps) {
  return (
    <RawButton
      onPress={onSelect}
      className={styles.couponItem}
      testId={'job-coupon-item-' + code}
    >
      <div className={styles.couponSelector}>
        <Icon
          name={isSelected ? 'radio-selected' : 'radio'}
          color="var(--brand-primary)"
        />
      </div>

      <div className={styles.couponContent}>
        <p className={styles.couponTitle}>
          <strong>{code}</strong>
          <span>
            {dateExpires ? ` • Expires on ${formatDate(dateExpires)}` : ''}
          </span>
        </p>

        {maxSavings && <p> Maximum savings of {maxSavings}</p>}

        {description && <p>{description}</p>}
      </div>

      <div className={styles.couponDiscount}>
        <span>{discount}</span>
      </div>
    </RawButton>
  )
}

function formatDate(date: Date) {
  return dateFormatters.dateLong.format(date)
}
